<template>
  <v-container fluid class="login-container d-flex align-center justify-center" :class="{ 'login-container--mobile-app': isMobileApp }">
    <v-row justify="center" align="center" class="fill-height">
      <v-col cols="12" sm="8" md="5" lg="4">
        <v-card class="login-card pa-6" :elevation="isMobileApp ? 0 : 8" rounded="xl">
          <div class="text-center mb-4">
            <h2 class="font-weight-bold mb-1">
              {{ mfaActive ? "Verify your identity" : $t("labels.WelcomeBack") }}
            </h2>
            <p class="grey--text">
              {{ mfaActive ? mfaSubtitle : "Login to continue to your dashboard" }}
            </p>
          </div>

          <template v-if="!mfaActive">
          <!-- Email -->
          <v-text-field
            v-model="email"
            outlined
            color="primary"
            label="Email"
            :error-messages="emailErrors"
            class="mb-3"
            autocomplete="username"
            @blur="normalizeEmail"
          />

          <!-- Password -->
          <v-text-field
            ref="password"
            v-model="password"
            outlined
            color="primary"
            :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            :type="showPassword ? 'text' : 'password'"
            :label="$t('labels.password')"
            :error-messages="passwordErrors"
            counter
            autocomplete="current-password"
            @keyup.enter.prevent="submit"
            @click:append="showPassword = !showPassword"
          />

          <!-- Login Button -->
          <v-btn
            block
            color="primary"
            large
            class="mt-4 v-btn-with-radius"
            data-cy="loginSubmitBtn"
            :loading="isSubmitting"
            :disabled="isSubmitting"
            @click.stop="submit"
          >
            Login
          </v-btn>

          <!-- Forgot Password -->
          <div class="text-center mt-3">
            <router-link
              data-cy="loginForgotPassword"
              to="/forgotPassword"
              style="text-decoration: none; color: #3677ff"
            >
              Forgot Password?
            </router-link>
          </div>

          <!-- Divider -->
          <v-divider v-if="!isMobileApp" class="my-6"></v-divider>

          <!-- Sign Up Redirect -->
          <div v-if="!isMobileApp" class="text-center">
            <span>Don't have an account?</span>
            <v-btn text color="pink" class="ml-1" @click="signUp">
              Sign up
            </v-btn>
          </div>
          </template>

          <template v-else>
            <v-alert v-if="mfaError" dense outlined type="error" role="alert" class="mb-4">
              {{ mfaError }}
            </v-alert>

            <div class="d-flex align-center justify-space-between mb-4">
              <h3 ref="mfaHeading" tabindex="-1" class="text-body-2 font-weight-medium flex-grow-1 mr-3">
                {{ activeChallenge.organizationName }} — step {{ mfaChallengeIndex + 1 }} of {{ mfaChallenges.length }}
              </h3>
              <v-chip small outlined color="primary" class="flex-shrink-0" aria-hidden="true">
                {{ mfaChallengeIndex + 1 }} of {{ mfaChallenges.length }}
              </v-chip>
            </div>

            <v-select
              v-if="mfaMethodItems.length > 1"
              v-model="mfaMethod"
              :items="mfaMethodItems"
              item-text="text"
              item-value="value"
              outlined
              dense
              label="Verification method"
              :disabled="mfaVerifying"
              @change="selectMfaMethod"
            />

            <p v-if="mfaMethod === 'totp'" class="text-body-2 mb-4">
              Open your authenticator app and enter its current 6-digit code.
            </p>

            <v-text-field
              ref="mfaCode"
              v-model.trim="mfaCode"
              outlined
              color="primary"
              :label="mfaMethod === 'recovery' ? 'Recovery code' : '6-digit verification code'"
              :inputmode="mfaMethod === 'recovery' ? 'text' : 'numeric'"
              :maxlength="mfaMethod === 'recovery' ? 32 : 6"
              :autocomplete="mfaMethod === 'recovery' ? 'off' : 'one-time-code'"
              :disabled="mfaVerifying"
              :error-messages="mfaCodeError ? [mfaCodeError] : []"
              @keyup.enter.prevent="verifyMfa"
            />

            <div class="d-flex flex-wrap align-center mt-n2 mb-3">
              <span v-if="mfaMethod === 'recovery'" class="text-caption grey--text">
                Enter one of the recovery codes you saved when MFA was set up. Each recovery code can be used once.
              </span>
            </div>

            <v-checkbox
              v-model="rememberDevice"
              class="mt-1 mb-1"
              color="primary"
              hide-details
              label="Remember this device for 30 days"
            />
            <p class="text-caption grey--text mb-4 ml-8">
              Use this only on a private device. Leave it unchecked on shared or public computers.
            </p>

            <v-btn v-if="!mfaReadyToSignIn"
              block
              color="primary"
              large
              class="v-btn-with-radius"
              :loading="mfaVerifying"
              :disabled="!canVerifyMfa || mfaVerifying"
              @click="verifyMfa"
            >
              Verify and continue
            </v-btn>
            <v-btn
              v-else
              block
              color="primary"
              large
              class="v-btn-with-radius"
              :loading="mfaVerifying"
              :disabled="mfaVerifying"
              @click="finalizeMfaLogin"
            >
              Continue to dashboard
            </v-btn>
            <v-btn block text class="mt-2" :disabled="mfaVerifying" @click="cancelMfa">
              Back to login
            </v-btn>
          </template>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import authService from "../service";
import { isNativeMobileApp } from "@/mobile/runtime";
import { refreshMobilePushRegistration } from "@/mobile/service";

const { validationMixin } = require("vuelidate");
const { required, email } = require("vuelidate/lib/validators");

export default {
  name: "authentication-login",
  data() {
    return {
      showPassword: false,
      email: null,
      password: null,
      isSubmitting: false,
      mfaChallenges: [],
      mfaChallengeIndex: 0,
      mfaAuthorizations: [],
      mfaMethod: "",
      mfaCode: "",
      mfaError: "",
      mfaCodeError: "",
      mfaVerifying: false,
      mfaReadyToSignIn: false,
      rememberDevice: false,
    };
  },
  async created() {
    if (isNativeMobileApp) {
      this.email = localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}rememberedEmail`) || null;
    }
    localStorage.removeItem(`${process.env.VUE_APP_STORAGE_PREFIX}token`);
  },
  beforeDestroy() {
    this.clearSensitiveLoginState();
  },
  methods: {
    ...mapActions("global", ["initOrganization"]),
    signUp() {
      this.$router.push({ path: "/signup" });
    },
    submit() {
      if (this.isSubmitting) return;
      this.normalizeCredentials();
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$toasted.error("Invalid Input", {
          position: "top-right",
          showHideTransition: "slide",
          duration: 2000,
        });
        return;
      }
      this.isSubmitting = true;
      this.$Progress.start();
      return authService
        .authenticate({ email: this.email, password: this.password })
        .then((response) => {
          this.$Progress.finish();
          this.isSubmitting = false;
          this.clearSensitiveLoginState();
          this.checkResponse(response);
        })
        .catch((error) => {
          this.$Progress.finish();
          this.isSubmitting = false;
          if (this.isMfaRequired(error)) return this.startMfa(this.mfaRequiredData(error));
          if (this.isConnectionFailure(error)) return this.showConnectionError();
          return this.showLoginError();
        });
    },
    normalizeEmail() {
      this.email = String(this.email || "").trim().toLowerCase();
    },
    normalizeCredentials() {
      this.normalizeEmail();
      this.password = String(this.password || "").trim();
    },
    isConnectionFailure(error) {
      if (!error) return false;
      const status = typeof error.status === "number"
        ? error.status
        : error.response && error.response.status;
      const hasHttpStatus = typeof status === "number" && Number.isFinite(status);
      return !hasHttpStatus && Boolean(error.request || error.isAxiosError);
    },
    mfaRequiredData(error) {
      const body = error && error.response && error.response.data
        ? error.response.data
        : error && error.data;
      return body && body.code === "MFA_REQUIRED" ? body.data : null;
    },
    isMfaRequired(error) {
      return Boolean(this.mfaRequiredData(error));
    },
    startMfa(payload) {
      const challenges = payload && Array.isArray(payload.challenges) ? payload.challenges : [];
      if (!challenges.length) return this.showLoginError();
      this.mfaChallenges = challenges;
      this.mfaChallengeIndex = 0;
      this.mfaAuthorizations = [];
      this.prepareChallenge();
    },
    prepareChallenge() {
      this.mfaCode = "";
      this.mfaError = "";
      this.mfaCodeError = "";
      this.mfaReadyToSignIn = false;
      const methods = this.mfaMethodItems.map((item) => item.value);
      this.mfaMethod = methods.includes("totp") ? "totp" : methods[0] || "";
      this.$nextTick(() => {
        if (this.$refs.mfaHeading) this.$refs.mfaHeading.focus();
      });
    },
    selectMfaMethod() {
      this.mfaCode = "";
      this.mfaError = "";
      this.mfaCodeError = "";
    },
    errorMessage(error, fallback) {
      const body = error && error.data ? error.data : error || {};
      if (body.code === "MFA_RATE_LIMITED") return body.message || "Too many requests. Please wait and try again.";
      return body.message || body.error || fallback;
    },
    errorData(error) {
      const body = error && error.data ? error.data : error || {};
      return body.data && typeof body.data === "object" ? body.data : body;
    },
    async verifyMfa() {
      if (!this.canVerifyMfa) return;
      this.mfaVerifying = true;
      this.mfaError = "";
      this.mfaCodeError = "";
      try {
        const response = await authService.verifyMfa({
          challengeId: this.activeChallenge.challengeId,
          method: this.mfaMethod,
          code: this.mfaCode,
        });
        const data = response.data || response;
        this.mfaAuthorizations.push(data.mfaAuthorization);
        if (this.mfaChallengeIndex < this.mfaChallenges.length - 1) {
          this.mfaChallengeIndex += 1;
          this.prepareChallenge();
          return;
        }
        this.mfaReadyToSignIn = true;
        this.mfaVerifying = false;
        await this.finalizeMfaLogin();
        return;
      } catch (error) {
        if (this.isMfaRequired(error)) {
          this.startMfa(this.mfaRequiredData(error));
          this.mfaError = "Verification expired. Complete the new verification request.";
          this.$nextTick(this.focusMfaCode);
        } else {
          const body = error && error.data ? error.data : {};
          const message = this.errorMessage(error, "The verification code is invalid or expired.");
          if (["MFA_CODE_INVALID", "MFA_CHALLENGE_INVALID"].includes(body.code)) this.mfaCodeError = message;
          else this.mfaError = message;
          this.$nextTick(this.focusMfaCode);
        }
      } finally {
        this.mfaVerifying = false;
      }
    },
    async finalizeMfaLogin() {
      if (!this.mfaReadyToSignIn || this.mfaVerifying) return;
      this.mfaVerifying = true;
      this.mfaError = "";
      try {
        const loginResponse = await authService.authenticate({
          email: this.email,
          password: this.password,
          mfaAuthorizations: this.mfaAuthorizations,
          trustDevice: this.rememberDevice,
        });
        this.clearSensitiveLoginState();
        this.checkResponse(loginResponse);
      } catch (error) {
        if (this.isMfaRequired(error)) {
          this.startMfa(this.mfaRequiredData(error));
          this.mfaError = "Verification expired. Complete the new verification request.";
        } else {
          this.mfaError = this.errorMessage(error, "Sign-in could not be completed. Your verification is saved; try continuing again.");
        }
      } finally {
        this.mfaVerifying = false;
      }
    },
    cancelMfa() {
      this.clearSensitiveLoginState();
      this.$nextTick(() => {
        if (this.$refs.password) this.$refs.password.focus();
      });
    },
    focusMfaCode() {
      if (!this.$refs.mfaCode) return;
      this.$refs.mfaCode.focus();
      const input = this.$refs.mfaCode.$refs && this.$refs.mfaCode.$refs.input;
      if (input && typeof input.select === "function") input.select();
    },
    clearSensitiveLoginState() {
      this.mfaChallenges = [];
      this.mfaChallengeIndex = 0;
      this.mfaAuthorizations = [];
      this.mfaMethod = "";
      this.mfaCode = "";
      this.mfaError = "";
      this.mfaCodeError = "";
      this.mfaReadyToSignIn = false;
      this.rememberDevice = false;
      this.password = null;
    },
    showLoginError() {
      return this.$swal({
        icon: "error",
        title: "Oops, we couldn’t sign you in",
        text: "Check your email and password and try again.",
        confirmButtonText: "Back to sign in",
      });
    },
    showConnectionError() {
      return this.$swal({
        icon: "error",
        title: "Connection problem",
        text: `${this.brandDisplayName} could not reach the server. Check your internet connection and try again.`,
        confirmButtonText: "Try again",
      });
    },
    checkResponse(response) {
      if (isNativeMobileApp && response.data && response.data.user) {
        localStorage.setItem(
          `${process.env.VUE_APP_STORAGE_PREFIX}rememberedEmail`,
          String(this.email || "").trim().toLowerCase()
        );
      }
      if (
        response.data &&
        response.data.user &&
        response.data.user.Organizations.length === 1
      ) {
        return this.initOrganization(response.data.user.Organizations[0]).then(() => {
          refreshMobilePushRegistration().catch((error) => {
            console.error("Unable to refresh mobile notification registration", error);
          });
          return this.$router.push({ path: "/" });
        });
      }
      if (
        response.data &&
        response.data.user &&
        response.data.user.Organizations.length > 1
      ) {
        this.$router.push({
          path: "/login/organization",
          name: "authentication-select-organization",
          params: { Organizations: response.data.user.Organizations },
        });
      } else if (response.data && response.data.user) {
        this.$router.push({ path: "/newOrganization" });
      } else {
        this.$toasted.error("Invalid Login Request", {
          position: "top-right",
          showHideTransition: "slide",
          duration: 2000,
        });
      }
    },
  },
  computed: {
    ...mapGetters("brand", ["currentBrand", "brandConfig"]),
    isMobileApp() {
      return isNativeMobileApp;
    },
    brandDisplayName() {
      return { repairnest: "RepairNest", storesync: "StoreSync POS", autorepair: "Auto Repair Desk" }[this.currentBrand] || "Back office";
    },
    mfaActive() {
      return this.mfaChallenges.length > 0;
    },
    activeChallenge() {
      return this.mfaChallenges[this.mfaChallengeIndex] || {};
    },
    mfaSubtitle() {
      return this.mfaChallenges.length > 1
        ? "Complete verification for each protected organization."
        : "This organization requires an extra verification step.";
    },
    mfaMethodItems() {
      const labels = { totp: "Authenticator app", recovery: "Recovery code" };
      const methods = Array.isArray(this.activeChallenge.methods)
        ? this.activeChallenge.methods.filter((method) => method === "totp")
        : [];
      if (this.activeChallenge.recoveryAvailable) methods.push("recovery");
      return methods.map(value => ({ value, text: labels[value] }));
    },
    canVerifyMfa() {
      if (!this.mfaMethod) return false;
      if (this.mfaMethod === "recovery") return this.mfaCode.length >= 6 && this.mfaCode.length <= 32;
      return /^\d{6}$/.test(this.mfaCode);
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.email.$dirty) return errors;
      if (!this.$v.email.email) errors.push("Email is not valid");
      if (!this.$v.email.required) errors.push("Email is required");
      return errors;
    },
    passwordErrors() {
      const errors = [];
      if (!this.$v.password.$dirty) return errors;
      if (!this.$v.password.required) errors.push("Password is required");
      return errors;
    },
  },
  mixins: [validationMixin],
  validations: {
    email: { email, required },
    password: { required },
  },
};
</script>

<style scoped>
.login-container--mobile-app {
  flex: 0 0 auto;
  max-width: 460px;
  padding: 0;
  width: 100%;
}
.login-container--mobile-app .row { margin: 0; width: 100%; }
.login-container--mobile-app .col { padding: 0; }
.login-container--mobile-app .login-card {
  background: var(--v-surface-base, var(--v-background-base));
  border: 1px solid rgba(0, 0, 0, 0.12);
  width: 100%;
}
.theme--dark .login-container--mobile-app .login-card { border-color: rgba(255, 255, 255, 0.2); }
@media (max-width: 599px) {
  .login-container--mobile-app .login-card { padding: 24px 20px !important; }
}
@media (max-height: 680px) {
  .login-container--mobile-app .login-card { padding-bottom: 16px !important; padding-top: 16px !important; }
  .login-container--mobile-app .login-card .text-center.mb-4 { margin-bottom: 8px !important; }
}
</style>
